import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline: 'border border-control-border bg-card text-foreground hover:bg-muted',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
        soft: 'bg-primary/10 text-primary hover:bg-primary/15',
        warning: 'border border-warning/30 bg-warning/10 text-warning-foreground hover:bg-warning/15',
        proposal: 'border border-proposal/30 bg-proposal-surface text-proposal hover:bg-proposal/10',
        destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/15',
        'destructive-ghost': 'text-destructive hover:bg-destructive/10 hover:text-destructive',
      },
      size: {
        default: 'min-h-11 px-4 py-2',
        sm: 'min-h-11 px-3 py-2 text-sm',
        lg: 'h-11 px-5',
        icon: 'size-11',
        inline: 'h-auto min-h-8 p-0 text-sm',
        tag: 'h-8 rounded-full px-2.5 py-1 text-sm',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants>;

export function Button({ className, variant, size, ...props }: ButtonProps) {
  return (
    <button
      type="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { buttonVariants };
